import { Link } from "react-router-dom";
import {
  Zap,
  Phone,
  Mail,
  MessageCircle,
  ArrowUpRight,
} from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/constants";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-navy-950 text-zinc-300 border-t-4 border-[#EAB308]"
    >
      {/* SIMPLE TOP STRIP */}
      <div className="border-b border-navy-800">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
              Ready when you are
            </div>
            <h3 className="mt-2 text-2xl md:text-3xl font-black tracking-tight text-white">
              Powering industries with confidence.
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] text-navy-950 px-5 py-3 font-bold uppercase tracking-wide text-xs border-2 border-[#EAB308]"
            >
              Start a Project
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-top"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-300 hover:text-[#22C55E]"
            >
              <MessageCircle className="h-4 w-4 text-[#22C55E]" />
              Or chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* MAIN GRID — clean 4-col */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-14 md:py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-5">
              <div className="relative h-12 md:h-14 w-auto">
                <img
                  src={COMPANY.logo}
                  alt={COMPANY.name || "Prime Volt"}
                  className="h-full w-auto object-contain max-w-[200px]"
                />
              </div>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Complete Electrical Panel Solutions — Design, Manufacturing,
              Installation & Maintenance for industries that demand reliability.
            </p>
          </div>

          {/* Sitemap */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308] mb-5">
              Sitemap
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.testId}`}
                    className="text-sm text-zinc-400 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Capabilities */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308] mb-5">
              Capabilities
            </h4>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li>MCC Panels</li>
              <li>PCC Panels</li>
              <li>PMCC Panels</li>
              <li>APFC Panels</li>
              <li>Control Panels</li>
              <li>SLD / Layout / AutoCAD</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308] mb-5">
              Contact
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a
                  href={`tel:${COMPANY.phone}`}
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-zinc-300 hover:text-white"
                >
                  <Phone className="h-4 w-4 mt-0.5 text-[#EAB308]" />
                  <span>{COMPANY.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-zinc-300 hover:text-white break-all"
                >
                  <Mail className="h-4 w-4 mt-0.5 text-[#EAB308]" />
                  <span>{COMPANY.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="inline-flex items-center gap-2 mt-2 bg-[#22C55E] hover:bg-[#16A34A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-navy-800 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-xs text-zinc-500 w-full md:w-auto text-center md:text-left">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <p className="text-base text-zinc-300 font-medium w-full md:w-auto text-center md:text-right tracking-wide">
            Crafted with <span className="inline-block animate-pulse text-red-500 mx-1">❤️</span> by{" "}
            <a
              href="https://www.smgwebsolutions.com/"
              target="_blank"
              rel="noreferrer"
              className="text-[#EAB308] hover:text-[#FACC15] transition-colors font-bold tracking-wider hover:underline decoration-2 underline-offset-4"
            >
              SMG Web Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
